// Based on https://github.com/daviddoran/typescript-reconnecting-websocket

import { CacheService } from "../../services/cache/cache.service";

// MIT License:

// Copyright (c) 2010-2012, Joe Walnes

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

/**
 * This behaves like a WebSocket in every way, except if it fails to connect,
 * or it gets disconnected, it will repeatedly poll until it succesfully connects
 * again.
 *
 * It is API compatible, so when you have:
 *   ws = new WebSocket('ws://....');
 * you can replace with:
 *   ws = new ReconnectingWebSocket('ws://....');
 *
 * It is API compatible with the standard WebSocket API.
 *
 * Latest version: https://github.com/joewalnes/reconnecting-websocket/
 * - Joe Walnes
 *
 * Latest TypeScript version: https://github.com/daviddoran/typescript-reconnecting-websocket/
 * - David Doran
 */
export class WebSocketWrapper {

    //These can be altered by calling code
    public debug:boolean = false;

    public receiveMessageInterval: number = 30000;
    public keepAliveInterval: number = 10000;

    public firstConnectInterval:number = 10000;
    //Time to wait before attempting reconnect (after close)
    public reconnectInterval:number = 30000;
    //Time to wait for WebSocket to open (before aborting and retrying)
    public timeoutInterval:number = 30000;

    //Should only be used to read WebSocket readyState
    public readyState:number;

    //Whether WebSocket was forced to close by this client
    private forcedClose:boolean = false;
    //Whether WebSocket opening timed out
    private timedOut:boolean = false;

    //List of WebSocket sub-protocols
    private protocols:string[] = [];

    //The underlying WebSocket
    private ws?:WebSocket;
    private urlPath:string;
    private urlParams:string;
    private httpRoute:string;

    private receiveMessageTimer: any;
    private keepAliveTimer: any;

    private static readonly keepAliveMessage = JSON.stringify({DataType: -1, Data: {}});
    /**
     * Setting this to true is the equivalent of setting all instances of EventsWebSocket.debug to true.
     */
    public static debugAll = false;

    //Set up the default 'noop' event handlers
    public onopen:(ev:Event) => void = function (event:Event) {};
    public onclose:(ev:CloseEvent) => void = function (event:CloseEvent) {};
    public onconnecting:() => void = function () {};
    public onmessage:(ev:MessageEvent) => void = function (event:MessageEvent) {};
    public onerror:(ev:Event) => void = function (event:Event) {};

    constructor(urlPath:string, urlParams:string, httpRoute:string, protocols:string[] = []) {
        this.urlPath = urlPath;
        this.urlParams = urlParams;
        this.httpRoute = httpRoute;
        this.protocols = protocols;
        this.readyState = WebSocket.CONNECTING;
        this.connect(false);
    }

    public changeHttpRoute(newHttpRoute:string)
    {
        if(newHttpRoute == this.httpRoute) return;
        this.httpRoute = newHttpRoute;
        this.connect(true);
    }

    public connect(reconnectAttempt:boolean) {
        if (reconnectAttempt && this.ws !== undefined) {
            delete this.ws;
            this.ws = undefined;
        }

        if(this.httpRoute == null || this.httpRoute == undefined)
        {
            return;
        }

        let url = this.urlPath + '/' + this.httpRoute + '?' + this.urlParams;

        this.ws = new WebSocket(url, this.protocols);
        console.log(url);

        this.onconnecting();
        this.log('EventsWebSocket', 'attempt-connect', url);

        var localWs = this.ws;
        var timeout = setTimeout(() => {
            this.log('EventsWebSocket', 'connection-timeout', url);
            this.timedOut = true;
            localWs.close();
            this.timedOut = false;
        }, this.timeoutInterval);

        this.ws.onopen = (event:Event) => {
            clearTimeout(timeout);
            this.readyState = WebSocket.OPEN;
            reconnectAttempt = false;
            this.initKeepAliveMessages();
            this.onopen(event);
        };

        this.ws.onclose = (event:CloseEvent) => {
            clearTimeout(timeout);
            this.ws = undefined;

            clearInterval(this.receiveMessageTimer);
            clearInterval(this.keepAliveTimer);

            if (this.forcedClose) {
                this.readyState = WebSocket.CLOSED;
                this.onclose(event);
            } else {
                this.readyState = WebSocket.CONNECTING;
                this.onconnecting();
                if (!reconnectAttempt && !this.timedOut) {
                    this.log('EventsWebSocket', 'onclose', url);
                    this.onclose(event);
                }
                setTimeout(() => {
                    this.connect(true);
                }, this.reconnectInterval);
            }
        };

        this.ws.onmessage = (event) => {
            this.heartbeat();

            if (event.data === WebSocketWrapper.keepAliveMessage) {
                return;
            }

            this.onmessage(event);
        };

        this.ws.onerror = (event) => {
            this.log('EventsWebSocket', 'onerror', url, event);
            this.onerror(event);
        };
    }

    public send(data:any) {
        if (this.ws && this.ws.readyState == WebSocket.OPEN) {
            return this.ws.send(data);
        } else {
            console.log('INVALID_STATE_ERR : Pausing to reconnect websocket');
        }
    }

    /**
     * Returns boolean, whether websocket was FORCEFULLY closed.
     */
    public close():boolean {
        if (this.ws) {
            this.forcedClose = true;
            this.ws.close();
            return true;
        }
        return false;
    }

    private initKeepAliveMessages(): void {
        this.keepAliveTimer = setInterval(() => {
            this.send(WebSocketWrapper.keepAliveMessage);
            this.log(WebSocketWrapper.keepAliveMessage, "sent");
        }, this.keepAliveInterval);
    }

    private heartbeat(): void {
        if (this.receiveMessageTimer) {
            clearInterval(this.receiveMessageTimer);
        }

        this.log('heartbeat');

        this.receiveMessageTimer = setInterval(this.reconnect, this.receiveMessageInterval);
    }

    private reconnect(): void {
        try {
            console.warn('receiveMessageTimeout elapsed');
            clearInterval(this.keepAliveTimer);
            if (this.ws) {
                console.warn('receiveMessageTimeout: closing web socket');
                this.ws.close();
            }
            console.warn('receiveMessageTimeout: reconnect web socket');

            this.connect(true);
        }
        catch(err) {}
    }

    private log(...args: any[]) {
        if (this.debug || WebSocketWrapper.debugAll) {
            console.log(args);
        }
    }
}
